import { Pipe, PipeTransform } from '@angular/core';

export type ChatMessageBlockType =
  | 'heading'
  | 'paragraph'
  | 'quote'
  | 'list'
  | 'ordered-list'
  | 'source-title'
  | 'source';
export type ChatMessageSegmentType = 'text' | 'strong' | 'emphasis' | 'code' | 'link';

export interface ChatMessageSegment {
  type: ChatMessageSegmentType;
  text: string;
  href?: string;
}

export interface ChatMessageBlock {
  type: ChatMessageBlockType;
  segments: ChatMessageSegment[];
  items: ChatMessageSegment[][];
}

const INLINE_TOKEN_PATTERN =
  /(`[^`]+`|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|\*([^*\n]+)\*|(https?:\/\/[^\s)]+))/g;
const SOURCE_TITLE_PATTERN = /^(Fuentes|Sources?|Fonti|Quellen)\s*:/i;

@Pipe({
  name: 'chatMessageFormat',
  standalone: true,
})
export class ChatMessageFormatPipe implements PipeTransform {
  transform(content: string, hideSources = false): ChatMessageBlock[] {
    const lines = this._getLines(content);
    const blocks: ChatMessageBlock[] = [];
    const paragraphLines: string[] = [];
    const quoteLines: string[] = [];
    const listItems: ChatMessageSegment[][] = [];
    let listType: 'list' | 'ordered-list' | null = null;
    let isSourceSection = false;

    const flushParagraph = () => {
      if (!paragraphLines.length) {
        return;
      }

      blocks.push(this._createBlock('paragraph', paragraphLines.join(' ')));
      paragraphLines.length = 0;
    };

    const flushList = () => {
      if (!listItems.length) {
        return;
      }

      blocks.push({
        type: listType ?? 'list',
        segments: [],
        items: [...listItems],
      });
      listItems.length = 0;
      listType = null;
    };

    const flushQuote = () => {
      if (!quoteLines.length) {
        return;
      }

      blocks.push(this._createBlock('quote', quoteLines.join(' ')));
      quoteLines.length = 0;
    };

    for (let index = 0; index < lines.length; index++) {
      const rawLine = lines[index];
      const line = rawLine.trim();
      const isIndentedLine = /^\s+/.test(rawLine);

      if (!line) {
        flushParagraph();
        flushQuote();
        continue;
      }

      if (this._isMarkdownSeparator(line)) {
        flushParagraph();
        flushQuote();
        flushList();
        continue;
      }

      if (this._isHeading(line)) {
        flushParagraph();
        flushQuote();
        flushList();
        blocks.push(this._createBlock('heading', line.replace(/^#{1,6}\s+/, '')));
        continue;
      }

      if (this._isNumberedSectionHeading(lines, index)) {
        flushParagraph();
        flushQuote();
        flushList();
        blocks.push(this._createBlock('heading', line));
        continue;
      }

      const quoteContent = this._getQuoteContent(rawLine);

      if (quoteContent !== null) {
        flushParagraph();
        flushList();
        quoteLines.push(quoteContent);
        continue;
      }

      flushQuote();

      const listItem = this._getListItem(line);

      if (listItem) {
        flushParagraph();
        if (listType && listType !== listItem.type) {
          flushList();
        }
        listType = listItem.type;
        listItems.push(this._parseInlineSegments(listItem.content));
        continue;
      }

      if (listItems.length && isIndentedLine) {
        this._appendToLatestListItem(listItems, line);
        continue;
      }

      if (this._isSourceTitle(line)) {
        flushParagraph();
        flushQuote();
        flushList();
        isSourceSection = true;
        if (!hideSources) {
          this._pushSourceTitle(blocks, line);
        }
        continue;
      }

      if (isSourceSection && this._isSourceLine(line)) {
        flushParagraph();
        flushQuote();
        flushList();
        if (!hideSources) {
          blocks.push(this._createBlock('source', line));
        }
        continue;
      }

      flushList();
      paragraphLines.push(line);
    }

    flushParagraph();
    flushQuote();
    flushList();

    return blocks;
  }

  private _getLines(content: string): string[] {
    return this._normalizeMarkdown(content)
      .split('\n')
      .map((line) => line.trimEnd());
  }

  private _normalizeMarkdown(content: string): string {
    return content
      .replace(/\\n/g, '\n')
      .replace(/\r\n?/g, '\n')
      .replace(/[ \t]+(#{1,6}\s+)/g, '\n\n$1')
      .replace(/[ \t]+(-\s+)/g, '\n$1')
      .replace(/[ \t]+(\d+[.)]\s+)/g, '\n$1')
      .replace(/[ \t]+((?:Fuentes|Sources?|Fonti|Quellen)\s*:)/gi, '\n\n$1')
      .replace(/\)\s+(\[https?:\/\/[^\]]+\]\(https?:\/\/[^)]+\))/g, ')\n$1')
      .trim();
  }

  private _isHeading(line: string): boolean {
    return /^#{1,6}\s+/.test(line);
  }

  private _isMarkdownSeparator(line: string): boolean {
    return /^#{1,6}$/.test(line) || /^([-*_])(?:\s*\1){2,}$/.test(line);
  }

  private _isNumberedSectionHeading(lines: string[], index: number): boolean {
    const line = lines[index].trim();

    if (!/^\d+[.)]\s+\S/.test(line)) {
      return false;
    }

    const previousLine = this._getPreviousContentLine(lines, index);

    if (previousLine && this._getListItem(previousLine)?.type === 'ordered-list') {
      return false;
    }

    const nextLine = this._getNextContentLine(lines, index);

    if (!nextLine) {
      return false;
    }

    return this._getListItem(nextLine)?.type !== 'ordered-list';
  }

  private _getPreviousContentLine(lines: string[], index: number): string | null {
    for (let currentIndex = index - 1; currentIndex >= 0; currentIndex--) {
      const line = lines[currentIndex].trim();

      if (!line || this._isMarkdownSeparator(line)) {
        continue;
      }

      return line;
    }

    return null;
  }

  private _getNextContentLine(lines: string[], index: number): string | null {
    for (let currentIndex = index + 1; currentIndex < lines.length; currentIndex++) {
      const line = lines[currentIndex].trim();

      if (!line || this._isMarkdownSeparator(line)) {
        continue;
      }

      return line;
    }

    return null;
  }

  private _getListItem(line: string): { type: 'list' | 'ordered-list'; content: string } | null {
    if (/^-\s+/.test(line)) {
      return { type: 'list', content: line.replace(/^-\s+/, '') };
    }

    if (/^\d+[.)]\s+/.test(line)) {
      return { type: 'ordered-list', content: line.replace(/^\d+[.)]\s+/, '') };
    }

    return null;
  }

  private _getQuoteContent(line: string): string | null {
    const quoteMatch = line.match(/^\s*>\s?(.*)$/);

    return quoteMatch ? quoteMatch[1].trim() : null;
  }

  private _appendToLatestListItem(listItems: ChatMessageSegment[][], content: string): void {
    const latestListItem = listItems.at(-1);

    if (!latestListItem) {
      return;
    }

    this._pushTextSegment(latestListItem, ' ');
    latestListItem.push(...this._parseInlineSegments(content));
  }

  private _isSourceTitle(line: string): boolean {
    return SOURCE_TITLE_PATTERN.test(line);
  }

  private _isSourceLine(line: string): boolean {
    return /^\[[^\]]+\]\(https?:\/\/[^)]+\)$/.test(line) || /^https?:\/\//.test(line);
  }

  private _pushSourceTitle(blocks: ChatMessageBlock[], line: string): void {
    const sourceTitle = line.match(SOURCE_TITLE_PATTERN)?.[0] ?? 'Fuentes:';
    const sourceText = line.replace(SOURCE_TITLE_PATTERN, '').trim();

    blocks.push(this._createBlock('source-title', sourceTitle));

    if (!sourceText) {
      return;
    }

    blocks.push(this._createBlock('source', sourceText));
  }

  private _createBlock(type: ChatMessageBlockType, content: string): ChatMessageBlock {
    return {
      type,
      segments: this._parseInlineSegments(content),
      items: [],
    };
  }

  private _parseInlineSegments(content: string): ChatMessageSegment[] {
    const segments: ChatMessageSegment[] = [];
    let cursor = 0;

    for (const match of content.matchAll(INLINE_TOKEN_PATTERN)) {
      const index = match.index ?? 0;
      this._pushTextSegment(segments, content.slice(cursor, index));
      segments.push(this._createInlineSegment(match[0], match[2], match[3], match[6]));
      cursor = index + match[0].length;
    }

    this._pushTextSegment(segments, content.slice(cursor));

    return segments.length ? segments : [{ type: 'text', text: content }];
  }

  private _pushTextSegment(segments: ChatMessageSegment[], text: string): void {
    if (!text) {
      return;
    }

    segments.push({ type: 'text', text });
  }

  private _createInlineSegment(
    token: string,
    linkText?: string,
    linkHref?: string,
    plainHref?: string,
  ): ChatMessageSegment {
    if (linkText && linkHref) {
      return { type: 'link', text: linkText, href: linkHref };
    }

    if (plainHref) {
      return { type: 'link', text: plainHref, href: plainHref };
    }

    if (token.startsWith('**')) {
      return { type: 'strong', text: token.slice(2, -2) };
    }

    if (token.startsWith('`')) {
      return { type: 'code', text: token.slice(1, -1) };
    }

    return { type: 'emphasis', text: token.slice(1, -1) };
  }
}
